import { getClient } from "@/data/client/getClient";
import { useSyncGatedQuery } from "@/hooks/useSyncGatedQuery";
import { appModeAtom } from "@/state/app.state";
import { userIdAtom } from "@/state/auth.state";
import { grainAtom, metricAtom } from "@/state/chart.state";
import {
  dateRangeAtom,
  limitAtom,
  queryAtom,
  sportsAtom,
} from "@/state/filters.state";
import type { ChartQueryOpts } from "@/types/chart.types";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

export function useChartQuery() {
  const mode = useAtomValue(appModeAtom);
  const userId = useAtomValue(userIdAtom);

  const search = useAtomValue(queryAtom);
  const sports = useAtomValue(sportsAtom);
  const limit = useAtomValue(limitAtom);
  const range = useAtomValue(dateRangeAtom);
  const metric = useAtomValue(metricAtom);
  const grain = useAtomValue(grainAtom);

  const q: ChartQueryOpts = useMemo(() => {
    const fromIso = range.from?.toISOString();
    const toInclusiveIso = range.to
      ? new Date(
          range.to.getFullYear(),
          range.to.getMonth(),
          range.to.getDate(),
          23,
          59,
          59,
          999,
        ).toISOString()
      : undefined;
    return {
      userId: userId ?? "",
      metric,
      grain,
      limit,
      search: search || undefined,
      sports: sports || undefined,
      from: fromIso,
      to: toInclusiveIso,
    };
  }, [userId, metric, grain, limit, search, sports, range.from, range.to]);

  return useSyncGatedQuery({
    queryKey: ["chart-data", q, mode],
    queryFn: () => getClient(mode).fetchChartData(q),
    placeholderData: (prev) => prev,
  });
}
